/**
 * DemoTour — URL-driven guided tour of the BizPulseAI Consulting Growth
 * Engine™. Renders as a floating overlay on top of the underlying page.
 *
 * Reuses EXISTING routes only:
 *   Step 1 · Dashboard              → /             (Landing shows the Engine)
 *   Step 2 · Campaign               → /c/demo       (public campaign landing)
 *   Step 3 · Assessment             → /c/demo       (same page — the diagnostic)
 *   Step 4 · Executive Growth Report → /sample-report
 *   Step 5 · Proposal               → /sample-report
 *   Step 6 · LinkedIn Assets        → /sample-report
 *   Step 7 · Nurture                → /sample-report
 *   Completion                      → same URL, overlay shows the celebration
 *
 * Activation: any page with `?demo=1&step=N` in the URL. The component is
 * mounted globally inside App.js so it can persist as the user navigates
 * between the reused routes.
 *
 * Zero backend, zero APIs, zero new pages, zero business-logic changes.
 */
import React, { useCallback, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { ArrowLeft, ArrowRight, PartyPopper, Rocket, X } from "lucide-react";

const STEPS = [
  {
    n: 1,
    title: "Dashboard",
    why: "Your consulting practice runs from a single dashboard — every stage of the Consulting Growth Engine™ orchestrated in one place.",
    to: "/",
  },
  {
    n: 2,
    title: "Campaign",
    why: "A branded campaign landing page — this is what pulls senior executives into your funnel on your terms, not through cold outreach.",
    to: "/c/demo",
  },
  {
    n: 3,
    title: "Assessment",
    why: "Executives complete a 10-minute business diagnostic tailored to their country, industry and function — the questions their board would ask.",
    to: "/c/demo",
  },
  {
    n: 4,
    title: "Executive Growth Report",
    why: "A grounded, cited report is generated — the artefact that builds instant credibility with senior operators on the first read.",
    to: "/sample-report",
  },
  {
    n: 5,
    title: "Proposal",
    why: "Turn any completed report into an engagement-ready proposal the same day — no blank page, no lost momentum, no cooling prospect.",
    to: "/sample-report",
  },
  {
    n: 6,
    title: "LinkedIn Assets",
    why: "Draft LinkedIn content straight from the report — so your practice stays visible between meetings without you writing another word.",
    to: "/sample-report",
  },
  {
    n: 7,
    title: "Nurture",
    why: "Automated email drips and follow-ups keep every lead warm until they are ready to sign — silence never loses another deal.",
    to: "/sample-report",
  },
];

export default function DemoTour() {
  const nav = useNavigate();
  const loc = useLocation();
  const params = useMemo(() => new URLSearchParams(loc.search), [loc.search]);

  const active = params.get("demo") === "1";
  const rawStep = params.get("step");

  const exitDemo = useCallback(() => {
    const p = new URLSearchParams(loc.search);
    p.delete("demo");
    p.delete("step");
    const search = p.toString();
    nav(`${loc.pathname}${search ? `?${search}` : ""}`, { replace: true });
  }, [nav, loc]);

  const goToStep = useCallback(
    (n) => {
      if (n === "complete") {
        // Stay on current page, just swap the step param.
        const p = new URLSearchParams(loc.search);
        p.set("demo", "1");
        p.set("step", "complete");
        nav(`${loc.pathname}?${p.toString()}`);
        return;
      }
      const target = STEPS.find((s) => s.n === n);
      if (!target) return;
      nav(`${target.to}?demo=1&step=${n}`);
    },
    [nav, loc.pathname, loc.search]
  );

  if (!active) return null;

  // ------------------------------------------------------------------
  // Completion overlay
  // ------------------------------------------------------------------
  if (rawStep === "complete") {
    return (
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-background/80 backdrop-blur-sm px-4"
        data-testid="demo-complete"
      >
        <Card className="card-elevated max-w-lg w-full p-8 relative">
          <button
            type="button"
            onClick={exitDemo}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition"
            aria-label="Close"
            data-testid="demo-complete-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center">
            <PartyPopper className="w-6 h-6" />
          </div>
          <h2
            className="mt-5 font-display text-3xl leading-tight"
            data-testid="demo-complete-headline"
          >
            Congratulations.
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            You have experienced the complete{" "}
            <span className="text-accent font-semibold">
              Consulting Growth Engine™
            </span>
            .
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" onClick={exitDemo} data-testid="demo-complete-trial">
              <Button size="lg" className="rounded-full px-6">
                Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing" onClick={exitDemo} data-testid="demo-complete-pricing">
              <Button size="lg" variant="outline" className="rounded-full px-6">
                View Pricing
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Step overlay
  // ------------------------------------------------------------------
  const stepNumber = Math.max(1, Math.min(7, parseInt(rawStep, 10) || 1));
  const step = STEPS.find((s) => s.n === stepNumber);
  const hasPrev = stepNumber > 1;
  const isLast = stepNumber === STEPS.length;

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[65] flex justify-center px-4 pointer-events-none"
      data-testid="demo-tour"
    >
      <Card className="card-elevated pointer-events-auto max-w-2xl w-full p-4 sm:p-5 border-accent/40 shadow-2xl">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-10 h-10 rounded-full bg-accent/15 text-accent items-center justify-center shrink-0">
            <Rocket className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="text-[10px] uppercase tracking-widest text-accent font-sans-ui"
                data-testid="demo-progress"
              >
                Step {stepNumber} of {STEPS.length}
              </span>
              <span className="text-xs text-muted-foreground font-sans-ui">
                · Consulting Growth Engine™
              </span>
            </div>
            <div
              className="mt-1 font-sans-ui font-semibold text-base text-foreground"
              data-testid="demo-title"
            >
              {step.title}
            </div>
            <p
              className="mt-1 text-sm text-muted-foreground leading-relaxed"
              data-testid="demo-why"
            >
              {step.why}
            </p>
            <div className="mt-2 h-1 rounded-full bg-border/60 overflow-hidden">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${(stepNumber / STEPS.length) * 100}%` }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={exitDemo}
            className="text-muted-foreground hover:text-foreground transition shrink-0"
            aria-label="Exit demo"
            data-testid="demo-exit"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasPrev}
            onClick={() => goToStep(stepNumber - 1)}
            data-testid="demo-prev"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Previous
          </Button>
          <Button
            size="sm"
            onClick={() => goToStep(isLast ? "complete" : stepNumber + 1)}
            data-testid="demo-next"
          >
            {isLast ? "Finish" : "Next"}
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
